/**
 * ConnectorDialog — the window every connector form opens in.
 *
 * The app's own modal shell (BotModal.css: .modal-overlay / .modal /
 * .modal-header), portalled to <body> like ConfirmModal so the agent editor's
 * transform cannot trap it, laid out as a WIDE rectangle in the Share-Agent
 * proportion: a fixed header, a body that scrolls inside the window, and a
 * footer that stays put with the action buttons. An antd Modal was used before;
 * it sat inside the editor's stacking context, grew taller than the viewport,
 * and scrolled the whole page with it.
 *
 * The overlay does not close the dialog — these are long forms and a stray
 * click outside must not throw one away.
 */

import { useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useDialogFocus, useFrontWindowEscape } from '@hooks/useDialogFocus';
import { useLanguage } from '@hooks/useLanguage';
import './BotModal.css';
import './ConnectorDialog.css';

export interface DialogProps {
  title: string;
  /** A line under the title; a node when it carries a control (the add window's "More"). */
  subtitle?: ReactNode;
  icon?: ReactNode;
  /** 'wide' — two equal columns; 'medium' — a form beside an aside; 'narrow' — one column. */
  size?: 'wide' | 'medium' | 'narrow';
  onClose: () => void;
  /** The footer's start side: an error, or a quiet status line. */
  note?: ReactNode;
  noteTone?: 'error' | 'muted';
  /** The footer's end side: the buttons. */
  actions: ReactNode;
  /** A risk acknowledgement — one checkbox line beside the buttons; Save
      waits for it (`RiskAcknowledgement`). */
  acknowledge?: ReactNode;
  testId?: string;
  children: ReactNode;
  /** Whether the form holds an edit not saved yet — for a frame that says so
      (the library entry's save footer); the dialog itself ignores it. */
  dirty?: boolean;
}

export function ConnectorDialog({
  title, subtitle, icon, size = 'wide', onClose, note, noteTone = 'muted', actions, acknowledge, testId, children,
}: DialogProps) {
  const { t } = useLanguage();
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Focus moves in, Tab stays in, and focus goes back to the opener on close.
  useDialogFocus(dialogRef, overlayRef);

  // Escape closes the TOPMOST window only. A confirmation can open over a
  // form (a save's consequences, "this makes it customized"); Escape there
  // answers the confirmation and must not also throw the form away. An open
  // select list or date picker closes first, on its own.
  useFrontWindowEscape(overlayRef, onClose);

  return createPortal(
    <div className="modal-overlay cxd-overlay" role="presentation" ref={overlayRef}>
      <div className={`modal cxd cxd--${size}`} role="dialog" aria-modal="true" aria-label={title} data-testid={testId}
           ref={dialogRef} tabIndex={-1}>
        <div className="modal-header">
          <div className="modal-header-content">
            <h2 className="modal-title cxd__title">
              {icon && <span className="cxd__title-icon" aria-hidden="true">{icon}</span>}
              <span dir="auto">{title}</span>
            </h2>
            {subtitle && <p className="modal-subtitle">{subtitle}</p>}
          </div>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label={t('common.close')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="cxd__body">{children}</div>

        <div className="cxd__footer">
          <div className="cxd__footer-start">
            {(note || !acknowledge) && (
              <div className={`cxd__note ${noteTone === 'error' && note ? 'is-error' : ''}`}
                   role={noteTone === 'error' && note ? 'alert' : undefined}>
                {note}
              </div>
            )}
            {acknowledge}
          </div>
          <div className="cxd__actions">{actions}</div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Thrown by a form backend when the person backed out of a confirmation the
 * backend asked (what saving will do, "this makes it customized"): the form
 * stops saving and shows no error.
 */
export class SaveCancelled extends Error {
  constructor() {
    super('cancelled');
    this.name = 'SaveCancelled';
  }
}

export const isSaveCancelled = (err: unknown): boolean => err instanceof SaveCancelled;

/**
 * The risk acknowledgement (item 69): one checkbox line beside Save, and Save
 * waits for it. `custom` — a connector added by hand (an MCP server or a REST
 * API, never a ready-made one); `write` — a save that switches on tools that
 * change data in `service`, once for all of them. The server refuses either
 * save without it and records who gave it, and when.
 */
const SERVICE_SLOT = '\u2063service\u2063';

export function RiskAcknowledgement({ risk, service, checked, onChange }: {
  risk: 'custom' | 'write';
  service?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const { t } = useLanguage();
  // The service's name is isolated, so "Jira — Alpha board" stays one piece inside a Hebrew sentence.
  const [before, after = ''] = t('bots.connectors.ack.write', { service: SERVICE_SLOT }).split(SERVICE_SLOT);
  return (
    <label className="cxd__ack" data-testid={`connector-ack-${risk}`}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span>{risk === 'custom' ? t('bots.connectors.ack.custom') : <>{before}<bdi dir="auto">{service}</bdi>{after}</>}</span>
    </label>
  );
}

/** Whether a save switches on a tool that changes data: on now, off when the form opened. */
export function switchesOnWrites(items: { writes?: boolean; was: boolean; now: boolean }[]): boolean {
  return items.some(i => i.writes && i.now && !i.was);
}

/**
 * The risk acknowledgement of one connector form: `custom` when it adds a
 * connector by hand, `write` when its save switches on tools that change data
 * (or the server said so — a new address can list new tools). `ok` gates Save,
 * `payload` goes into the save, `node` beside Save, `onError` reads a refusal.
 */
export function useRiskAcknowledgement({ customAdd, switchesOn, service }: {
  customAdd: boolean; switchesOn: boolean; service?: string;
}) {
  const [ticked, setTicked] = useState(false);
  const [serverAsked, setServerAsked] = useState(false);
  const asksWrite = !customAdd && (switchesOn || serverAsked);
  const asks = customAdd || asksWrite;
  return {
    ok: !asks || ticked,
    payload: customAdd ? { acknowledge_risk: true } : asksWrite ? { acknowledge_write: true } : {},
    node: asks
      ? <RiskAcknowledgement risk={customAdd ? 'custom' : 'write'} service={service} checked={ticked} onChange={setTicked} />
      : undefined,
    onError: (err: unknown) => {
      if (connectorErrorBody(err).error_code === 'WRITE_NOT_ACKNOWLEDGED') { setServerAsked(true); setTicked(false); }
    },
    reset: () => setTicked(false),
  };
}

/** A connector endpoint's error body (`response.data`), or {}. */
export function connectorErrorBody(err: unknown): {
  reason?: string; error?: string; error_code?: string; field?: string; tools?: string[]; service?: string;
} {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  return data && typeof data === 'object' ? data : {};
}

/** The connector endpoints' machine `reason` / `error_code` → the sentence the editor reads
    (MCP and API alike: the codes do not overlap). */
const REASON_KEYS: Record<string, string> = {
  RISK_NOT_ACKNOWLEDGED: 'bots.connectors.ack.customMissing',
  WRITE_NOT_ACKNOWLEDGED: 'bots.connectors.ack.writeMissing',
  unreachable: 'bots.connectors.mcp.errors.unreachable',
  private_host: 'bots.connectors.mcp.errors.privateHost',
  scheme: 'bots.connectors.mcp.errors.scheme',
  unresolvable: 'bots.connectors.mcp.errors.unresolvable',
  spec: 'bots.connectors.api.errors.spec',
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** A connector endpoint's refusal, worded: a known reason, else the
    server's own sentence, else the caller's fallback. */
export function phraseConnectorError(t: Translate, err: unknown, fallbackKey: string): string {
  const body = connectorErrorBody(err);
  const code = body.reason ?? body.error_code;
  const key = code ? REASON_KEYS[code] : undefined;
  return key ? t(key) : body.error || t(fallbackKey);
}

/**
 * Where a connector form is drawn. By default the form opens in its own
 * dialog; the connector library's entry window passes a frame of its own,
 * so the same form — the same fields, rules and saves — sits inside that
 * window's Settings tab with its buttons in the window's footer, never as a
 * window over a window.
 */
export type FormFrame = (props: DialogProps) => ReactNode;

export const dialogFrame: FormFrame = (props) => <ConnectorDialog {...props} />;

export function DialogColumns({ children }: { children: ReactNode }) {
  return <div className="cxd__columns">{children}</div>;
}

interface ColumnProps {
  title?: string;
  badge?: string;
  aside?: boolean;
  children: ReactNode;
}

export function DialogColumn({ title, badge, aside, children }: ColumnProps) {
  return (
    <div className={`cxd__column ${aside ? 'cxd__column--aside' : ''}`}>
      {title && (
        <div className="cxd__column-head">
          <h3 className="cxd__column-title">{title}</h3>
          {badge && <span className="cxd__column-badge">{badge}</span>}
        </div>
      )}
      {children}
    </div>
  );
}

/** A labelled field in the app's form idiom: label, optional hint, control. */
export function Field({ label, hint, htmlFor, children }: {
  label: ReactNode; hint?: ReactNode; htmlFor?: string; children: ReactNode;
}) {
  return (
    <div className="form-group">
      <label className="form-label" htmlFor={htmlFor}>{label}</label>
      {hint && <p className="form-help-text">{hint}</p>}
      {children}
    </div>
  );
}

/** A value shown, not edited — e.g. an inherited entry's address, "Set by <owner>". */
export function ReadOnlyValue({ children, mono, testId }: { children: ReactNode; mono?: boolean; testId?: string }) {
  return (
    <div className={`cxd__readonly ${mono ? 'cxd__readonly--mono' : ''}`} dir={mono ? 'ltr' : undefined}
         data-testid={testId}>
      {children}
    </div>
  );
}

/** Save/Connect button with the app's spinner while busy. */
export function PrimaryAction({ label, busyLabel, busy, disabled, onClick, testId }: {
  label: string; busyLabel?: string; busy?: boolean; disabled?: boolean; onClick: () => void; testId?: string;
}) {
  return (
    <button type="button" className="cx-btn cx-btn--primary cx-btn--lg" disabled={disabled || busy}
            onClick={onClick} data-testid={testId}>
      {busy ? (
        <span className="loading-indicator">
          <span className="loading-spinner" />
          <span>{busyLabel ?? label}</span>
        </span>
      ) : label}
    </button>
  );
}

export function PlusGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/** A Connectors-tab section heading: icon, title, description, its own action. */
export function SectionHead({ icon, title, description, action }: {
  icon: ReactNode; title: string; description: string; action?: ReactNode;
}) {
  return (
    <div className="cxs__head">
      <div className="cxs__heading">
        <span className="cxs__icon" aria-hidden="true">{icon}</span>
        <div>
          <h4 className="cxs__title">{title}</h4>
          <p className="cxs__desc">{description}</p>
        </div>
      </div>
      {action && <div className="cxs__action">{action}</div>}
    </div>
  );
}
